import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { request } from '@/lib/api.js';
import { useFmt } from '@/lib/useFmt.js';
import { GlassButton, useConfirm } from '@/components/ui/index.js';
import { AnalyticsSection } from './userDetail/AnalyticsSection.js';
import { DevicesCard } from './userDetail/DevicesCard.js';
import { AuditLogsList } from './userDetail/AuditLogsList.js';
import type {
  GrantSubscriptionResponse,
  ResetPasswordResponse,
  UserDetailResponse,
} from '@/lib/types.js';

const TIER_LABEL = {
  free: '免费',
  standard: '标准',
  pro: 'Pro',
  premium: 'Premium',
} as const;

export function UserDetailPage(): JSX.Element {
  const confirm = useConfirm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<UserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [tempPasswordCopied, setTempPasswordCopied] = useState(false);
  const [actionNotice, setActionNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const { fmtDateTime, fmtDate } = useFmt();

  const load = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    setLoading(true);
    setError(null);
    try {
      const res = await request<UserDetailResponse>(`/admin/users/${id}`);
      setDetail(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleGrant(plan: 'monthly' | 'yearly'): Promise<void> {
    if (id === undefined) return;
    // 与 UsersPage 同操作一致(Audit B5: 改档操作需确认); grant 只增不减、立即生效。
    const ok = await confirm({
      title: '增加会员',
      message: `确认给该用户增加${plan === 'monthly' ? '一个月' : '一年'}会员？立即生效，且只增不减、无法在此撤回。`,
      confirmLabel: '增加',
      tone: 'warning',
    });
    if (!ok) return;
    setAction(`grant_${plan}`);
    setActionNotice(null);
    try {
      const res = await request<GrantSubscriptionResponse>(
        `/admin/users/${id}/grant_subscription`,
        { method: 'POST', body: { plan } },
      );
      setActionNotice({
        kind: 'success',
        text: `已充值${plan === 'monthly' ? '月卡' : '年卡'}，到期：${fmtDateTime(res.newExpiresAt)}`,
      });
      await load();
    } catch (err) {
      setActionNotice({ kind: 'error', text: err instanceof Error ? err.message : '操作失败' });
    } finally {
      setAction(null);
    }
  }

  async function handleResetPassword(): Promise<void> {
    if (id === undefined) return;
    const ok = await confirm({
      title: '重置密码',
      message: '确定重置该用户的密码？旧密码将立即失效，需要把新临时密码通过私聊给用户。',
      confirmLabel: '重置',
      tone: 'danger',
    });
    if (!ok) return;
    setAction('reset_password');
    setActionNotice(null);
    setTempPassword(null);
    try {
      const res = await request<ResetPasswordResponse>(
        `/admin/users/${id}/reset_password`,
        { method: 'POST' },
      );
      setTempPassword(res.temp_password);
      setActionNotice({ kind: 'success', text: '已生成新临时密码，请复制后通过私聊发给用户。' });
      await load();
    } catch (err) {
      setActionNotice({ kind: 'error', text: err instanceof Error ? err.message : '操作失败' });
    } finally {
      setAction(null);
    }
  }

  async function handleForceLogout(): Promise<void> {
    if (id === undefined) return;
    const ok = await confirm({
      title: '强制登出',
      message: '强制登出该用户所有设备？',
      confirmLabel: '强制登出',
      tone: 'danger',
    });
    if (!ok) return;
    setAction('force_logout');
    setActionNotice(null);
    try {
      await request<{ ok: true }>(`/admin/users/${id}/force_logout`, { method: 'POST' });
      setActionNotice({ kind: 'success', text: '已强制登出。用户当前 access token 失效后无法刷新。' });
      await load();
    } catch (err) {
      setActionNotice({ kind: 'error', text: err instanceof Error ? err.message : '操作失败' });
    } finally {
      setAction(null);
    }
  }

  async function handleForceVerify(): Promise<void> {
    if (id === undefined) return;
    setAction('force_verify');
    setActionNotice(null);
    try {
      await request<{ ok: true }>(`/admin/users/${id}/verify_email`, { method: 'POST' });
      setActionNotice({ kind: 'success', text: '已强制标记邮箱已验证。' });
      await load();
    } catch (err) {
      setActionNotice({ kind: 'error', text: err instanceof Error ? err.message : '操作失败' });
    } finally {
      setAction(null);
    }
  }

  if (loading) return <div className="text-slate-400">加载中…</div>;
  if (error !== null) return <div className="text-rose-300">{error}</div>;
  if (detail === null) return <div className="text-slate-400">用户不存在</div>;

  const { user, subscription, devices, audit_logs, analytics } = detail;

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/users')}
        className="text-sm text-slate-400 hover:text-shark-300 mb-4 inline-flex items-center gap-1 transition-colors"
      >
        ← 返回用户列表
      </button>

      <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight mb-1">{user.email ?? user.phone ?? user.id}</h1>
      <p className="text-sm text-slate-500 mb-6 font-mono">{user.id}</p>

      {actionNotice !== null && (
        <div
          className={`text-sm rounded-lg px-3 py-2 mb-4 ${
            actionNotice.kind === 'success'
              ? 'text-felt-400 bg-felt-700/20 border border-felt-500/40'
              : 'text-rose-300 bg-rose-900/30 border border-rose-700/50'
          }`}
        >
          {actionNotice.text}
        </div>
      )}

      {tempPassword !== null && (
        <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg px-4 py-3 mb-4">
          <p className="text-xs text-amber-300 mb-1 font-semibold">
            🔑 新临时密码 — 仅这次显示。
            <span className="underline decoration-amber-400">复制好之后再点「我已收好」关闭</span>
            。
          </p>
          <div className="flex items-center gap-2 my-2">
            <code className="flex-1 font-mono text-amber-200 bg-amber-950/40 px-3 py-2 rounded select-all">
              {tempPassword}
            </code>
            <GlassButton
              size="sm"
              variant={tempPasswordCopied ? 'primary' : 'ghost'}
              onClick={() => {
                void navigator.clipboard.writeText(tempPassword);
                setTempPasswordCopied(true);
              }}
            >
              {tempPasswordCopied ? '✓ 已复制' : '复制'}
            </GlassButton>
          </div>
          <div className="flex items-center justify-between gap-2 mt-3">
            <span className="text-xs text-amber-200/60">
              {tempPasswordCopied
                ? '密码已在剪贴板。私聊发给用户后再关闭。'
                : '注意：关闭后无法再次查看。'}
            </span>
            <GlassButton
              size="sm"
              variant="ghost"
              disabled={!tempPasswordCopied}
              onClick={() => {
                setTempPassword(null);
                setTempPasswordCopied(false);
              }}
            >
              我已收好，关闭
            </GlassButton>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <section className="mat-matte rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-300 mb-3">基本信息</h2>
          <dl className="space-y-2 text-sm">
            <Row label="邮箱" value={user.email} verified={user.email_verified} />
            <Row label="手机号" value={user.phone} verified={user.phone_verified} />
            <Row label="昵称" value={user.display_name} />
            <Row label="注册时间" value={fmtDateTime(user.created_at)} />
            <Row label="最近登录" value={user.last_login_at !== null ? fmtDateTime(user.last_login_at) : '从未'} />
            <Row label="首次付费" value={user.first_purchase_at !== null ? fmtDateTime(user.first_purchase_at) : '未付费'} />
            <Row label="连续订阅" value={user.continuous_subscription ? '是（享 grandfathered 价）' : '否'} />
          </dl>
        </section>

        <section className="mat-matte rounded-xl p-5">
          <h2 className="text-sm font-semibold text-slate-300 mb-3">订阅状态</h2>
          {subscription !== null ? (
            <dl className="space-y-2 text-sm">
              <Row label="档位" value={TIER_LABEL[subscription.tier]} />
              <Row label="套餐" value={subscription.plan === 'monthly' ? '月卡' : subscription.plan === 'yearly' ? '年卡' : '—'} />
              <Row label="到期时间" value={subscription.expires_at !== null ? fmtDateTime(subscription.expires_at) : '—'} />
              <Row label="激活方式" value={subscription.granted_via ?? '—'} />
              <Row label="付款价格" value={subscription.price_at_purchase !== null ? `¥${(subscription.price_at_purchase / 100).toFixed(2)}` : '—'} />
              <Row label="更新时间" value={fmtDateTime(subscription.updated_at)} />
            </dl>
          ) : (
            <p className="text-sm text-slate-500">免费用户，无订阅</p>
          )}
        </section>
      </div>

      <section className="mat-matte rounded-xl p-5 mb-6">
        <h2 className="text-sm font-semibold text-slate-300 mb-3">管理操作</h2>
        <div className="flex flex-wrap gap-2">
          <ActionBtn busy={action === 'grant_monthly'} onClick={(): void => { void handleGrant('monthly'); }}>
            +1 月会员
          </ActionBtn>
          <ActionBtn busy={action === 'grant_yearly'} onClick={(): void => { void handleGrant('yearly'); }}>
            +1 年会员
          </ActionBtn>
          <ActionBtn busy={action === 'reset_password'} onClick={(): void => { void handleResetPassword(); }}>
            重置密码
          </ActionBtn>
          <ActionBtn busy={action === 'force_logout'} onClick={(): void => { void handleForceLogout(); }}>
            强制登出
          </ActionBtn>
          {!user.email_verified && user.email !== null && (
            <ActionBtn busy={action === 'force_verify'} onClick={(): void => { void handleForceVerify(); }}>
              强制邮箱已验证
            </ActionBtn>
          )}
        </div>
      </section>

      <AnalyticsSection analytics={analytics} fmtDate={fmtDate} fmtDateTime={fmtDateTime} />
      <DevicesCard devices={devices} fmtDateTime={fmtDateTime} />
      <AuditLogsList logs={audit_logs} fmtDate={fmtDate} fmtDateTime={fmtDateTime} />
    </div>
  );
}

function Row({
  label,
  value,
  verified,
}: {
  label: string;
  value: string | null | undefined;
  verified?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500 min-w-[80px]">{label}</dt>
      <dd className="text-slate-200 flex items-center gap-2">
        <span>{value ?? <span className="text-slate-600">—</span>}</span>
        {value !== null && verified !== undefined && (
          verified ? (
            <span className="text-xs text-felt-400">✓ 已验证</span>
          ) : (
            <span className="text-xs text-amber-300">未验证</span>
          )
        )}
      </dd>
    </div>
  );
}

function ActionBtn({
  busy,
  onClick,
  children,
}: {
  busy: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <GlassButton size="md" variant="ghost" disabled={busy} onClick={onClick}>
      {busy ? '处理中…' : children}
    </GlassButton>
  );
}

